import React, { useState } from "react";
import PropTypes from "prop-types";
import { styled, alpha } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import InputBase from "@mui/material/InputBase";
import { Icon } from "@iconify/react";
import MembersFilterSidebar from "./MembersFilterSidebar";
import MemberSort from "./MemberSort";
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Chip,
  InputAdornment,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { setDialog } from "../../../features/dialog/dialogSlice";
import { gradArray } from "../../../components/form/fields/inputFields";
import useAuth from "../../../hooks/useAuth";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": { backgroundColor: alpha(theme.palette.common.white, 0.25) },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    [theme.breakpoints.up("sm")]: {
      width: "12ch",
      "&:focus": { width: "20ch" },
    },
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const STATUS_LABELS = {
  active: "Aktiv",
  frozen: "Eingefroren",
  terminated: "Gekündigt",
  pending: "Wartend",
  rejected: "Abgelehnt",
};

function MemberTableTopBar({
  handleSearchTerm,
  searchTerm,
  handleSort,
  tableColumns,
  handleChangeTableColumns,
  // selection + actions
  selectionCount = 0,
  onClearSelection,
  onBulkDelete,
  onBulkAddTestingList,
  onBulkAddEvent,
  onBulkEdit,
  // filters
  filters,
  onChangeFilters,
}) {
  const [openFilter, setOpenFilter] = useState(false);
  const [anchorElNav, setAnchorElNav] = useState(null);
  const dispatch = useDispatch();
  const { isAdmin, isEditor, isSuperAdmin } = useAuth();
  const canCreateMember = isAdmin || isEditor || isSuperAdmin;
  const canBulkEdit = isAdmin || isEditor || isSuperAdmin;

  const handleOpenMemberForm = () => {
    dispatch(setDialog({ title: "Neues Mitglied erstellen", type: "CREATE" }));
  };

  const handleOpenFilter = () => setOpenFilter(true);
  const handleCloseFilter = () => setOpenFilter(false);
  const handleOpenNavMenu = (event) => setAnchorElNav(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);
  const onSort = (value, sort) => {
    handleSort(value, sort);
    handleCloseNavMenu();
  };

  // --------- Active filter chips ----------
  const renderFilterChips = () => {
    const chips = [];

    if (filters?.sites?.length) {
      chips.push(
        <Chip
          key="chip-sites"
          color="info"
          size="small"
          label={`Standort: ${filters.sites.length} ausgewählt`}
          onDelete={() => onChangeFilters({ ...filters, sites: [] })}
          sx={{ mr: 1 }}
        />
      );
    }

    if (filters?.degrees?.length) {
      const labels = filters.degrees
        .map((d) => gradArray[d]?.label)
        .filter(Boolean)
        .join(", ");
      chips.push(
        <Chip
          key="chip-degrees"
          color="info"
          size="small"
          label={`Grad: ${labels}`}
          onDelete={() => onChangeFilters({ ...filters, degrees: [] })}
          sx={{ mr: 1 }}
        />
      );
    }

    if (filters?.statuses?.length) {
      const labels = filters.statuses
        .map((s) => STATUS_LABELS[s] || s)
        .join(", ");
      chips.push(
        <Chip
          key="chip-statuses"
          color="info"
          size="small"
          label={`Status: ${labels}`}
          onDelete={() => onChangeFilters({ ...filters, statuses: ["active"] })}
          sx={{ mr: 1 }}
        />
      );
    }

    return chips.length ? (
      <Stack direction="row" spacing={0.5} sx={{ mr: 2 }}>
        {chips}
      </Stack>
    ) : null;
  };

  const BulkActions = () =>
    selectionCount > 0 ? (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
        <Chip
          label={`${selectionCount} ausgewählt`}
          color="primary"
          onDelete={onClearSelection}
          deleteIcon={<Icon icon="mdi:close" />}
        />
        {selectionCount > 1 && (
          <Button
            variant="contained"
            color="error"
            onClick={onBulkDelete}
            startIcon={<Icon icon="jam:trash" />}
          >
            Löschen
          </Button>
        )}
        <Button
          variant="contained"
          color="secondary"
          onClick={onBulkAddTestingList}
          startIcon={<Icon icon="material-symbols:assignment-add" />}
        >
          Zu Prüfungsliste
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={onBulkAddEvent}
          startIcon={<Icon icon="mdi:calendar-plus" />}
        >
          Zu Veranstaltung
        </Button>
        {canBulkEdit && (
          <Button
            variant="contained"
            color="primary"
            onClick={onBulkEdit}
            startIcon={<Icon icon="dashicons:edit-page" />}
          >
            Mehrere bearbeiten
          </Button>
        )}
      </Stack>
    ) : (
      renderFilterChips()
    );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="sticky"
        style={{
          backgroundColor: "black",
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
        }}
      >
        <Toolbar>
          {/* Mobile menu */}
          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <Icon icon="ic:round-menu" width="24" height="24" />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: "block", md: "none" } }}
            >
            {canCreateMember && (
              <MenuItem onClick={handleCloseNavMenu}>
                <Button
                  variant="contained"
                  color="secondary"
                  endIcon={<Icon width="26" icon="fa-solid:user-plus" />}
                  sx={{ mr: 2 }}
                  onClick={() => handleOpenMemberForm()}
                >
                  Neues Mitglied
                </Button>
              </MenuItem>
            )}
              <MenuItem onClick={handleCloseNavMenu}>
                <MembersFilterSidebar
                  openFilter={openFilter}
                  onOpenFilter={handleOpenFilter}
                  onCloseFilter={handleCloseFilter}
                  tableColumns={tableColumns}
                  handleChangeTableColumns={handleChangeTableColumns}
                  filters={filters}
                  onChangeFilters={onChangeFilters}
                />
              </MenuItem>
              <MenuItem>
                <MemberSort onSort={onSort} />
              </MenuItem>
            </Menu>
          </Box>

          {/* Desktop left actions */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "none", md: "flex" },
              alignItems: "center",
            }}
          >
            {canCreateMember && (
              <Button
                variant="contained"
                color="secondary"
                endIcon={<Icon width="26" icon="fa-solid:user-plus" />}
                sx={{ mr: 2 }}
                onClick={() => handleOpenMemberForm()}
              >
                Neues Mitglied
              </Button>
            )}
            <MembersFilterSidebar
              openFilter={openFilter}
              onOpenFilter={handleOpenFilter}
              onCloseFilter={handleCloseFilter}
              tableColumns={tableColumns}
              handleChangeTableColumns={handleChangeTableColumns}
              filters={filters}
              onChangeFilters={onChangeFilters}
            />
            <MemberSort onSort={onSort} />
          </Box>

          {/* Title or bulk/filter chips */}
          {selectionCount > 0 ? (
            <BulkActions />
          ) : (
            <>
              <BulkActions />
              <Typography
                variant="h5"
                noWrap
                component="div"
                sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }}
              >
                MITGLIEDERÜBERSICHT
              </Typography>
            </>
          )}

          {/* Search */}
          <Search>
            <SearchIconWrapper>
              <Icon icon="material-symbols:search" width="24" height="24" />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Suchen..."
              inputProps={{ "aria-label": "search" }}
              value={searchTerm}
              endAdornment={
                <InputAdornment
                  style={{
                    display: searchTerm === "" ? "none" : "flex",
                    margin: 0,
                  }}
                  onClick={() => handleSearchTerm({ target: { value: "" } })}
                  position="end"
                >
                  <IconButton
                    aria-label={"Zurücksetzen"}
                    edge="end"
                    sx={{ m: 0 }}
                  >
                    <Icon icon="fontisto:close" />
                  </IconButton>
                </InputAdornment>
              }
              onChange={(e) => handleSearchTerm(e)}
            />
          </Search>
        </Toolbar>
      </AppBar>
    </Box>
  );
}

MemberTableTopBar.propTypes = {
  handleSearchTerm: PropTypes.func,
  searchTerm: PropTypes.string,
  handleSort: PropTypes.func,
  tableColumns: PropTypes.array,
  handleChangeTableColumns: PropTypes.func,
  selectionCount: PropTypes.number,
  onClearSelection: PropTypes.func,
  onBulkDelete: PropTypes.func,
  onBulkAddTestingList: PropTypes.func,
  onBulkAddEvent: PropTypes.func,
  filters: PropTypes.object,
  onChangeFilters: PropTypes.func,
};

export default MemberTableTopBar;
